import React, { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import {
  SwipeableListItem as RawSwipeableListItem,
  LeadingActions,
  TrailingActions,
  SwipeAction as RawSwipeAction,
  Type,
} from 'react-swipeable-list'
import 'react-swipeable-list/dist/styles.css'
import '../../../../../generic/swipe/swipeable-overrides.css'
import {
  SWIPE_START_THRESHOLD,
  SCROLL_START_THRESHOLD,
} from '../../../../../generic/swipe/constants'
import { statusConverter } from '../../../../../generic/artcoded/statusAzmanot'
import { LINKS } from '../../../../../Routs/config'
import { OrderRow } from '../../../../../generic/ui/OrderRow'
import { SpeedBadge } from '../../../../../generic/ui/SpeedBadge'
import { SwipeAction } from '../../../../../generic/ui/SwipeAction'
import TrashIcon from '../../../../../generic/icons/IconLib/TrashIcon'
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon'
import EyeIcon from '../../../../../generic/icons/IconLib/EyeIcon'
import PrintIcon from '../../../../../generic/icons/IconLib/PrintIcon'
import OrderDeleteConfirm from './OrderDeleteConfirm'
import { OrderItem } from '../types'

type Props = OrderItem & {
  removeAzmanot: (orderID: number) => Promise<void>
  setInnerTime?: (value: string) => void
  onPrint?: (orderID: number) => void
  index?: number
  isLast?: boolean
  tagColor?: string
}

const OVERLAY_THRESHOLD = 30 // % — open action overlay
const TRIGGER_THRESHOLD = 60 // % — fire primary action directly

const noop = () => {}

const formatDeliveryDate = (raw?: string): string => {
  if (!raw || raw.length < 8) return ''
  const month = raw.slice(4, 6)
  const day = raw.slice(6, 8)
  return `${day}/${month}`
}

const OrderRowItem: React.FC<Props> = ({
  removeAzmanot,
  onPrint,
  Customer_Name,
  OrderID,
  Delivery_Date,
  Status,
  GroupDesc,
  Type: OrderType,
  isLast = false,
  tagColor,
}) => {
  const [confDelete, setConfDelete] = useState(false)
  const [editOverlay, setEditOverlay] = useState(false)
  const lastProgress = useRef(0)
  const lastDirection = useRef<'left' | 'right' | ''>('')
  const navigate = useNavigate()

  const { modalEditText, statusInfo, canEdit } = useMemo(() => {
    const { status, color } = statusConverter(Status)
    return {
      modalEditText: Status === 0 ? 'עדכון' : 'עיון',
      statusInfo: status ? { label: status, bg: color } : undefined,
      canEdit: Status === 0,
    }
  }, [Status])

  const date = useMemo(() => formatDeliveryDate(Delivery_Date), [Delivery_Date])
  const tag = GroupDesc?.trim() ? GroupDesc.trim() : undefined

  const canDelete = canEdit
  const canPrint = !!onPrint

  const handleNavigate = () => {
    setEditOverlay(false)
    setConfDelete(false)
    navigate(`${LINKS.orders.clientOrders.editByID}${OrderID}`)
  }

  const handlePrint = () => {
    if (!onPrint) return
    setEditOverlay(false)
    setConfDelete(false)
    onPrint(OrderID)
  }

  const handleConfirmDelete = () => {
    setConfDelete(false)
    removeAzmanot(OrderID)
  }

  const handleSwipeStart = () => {
    setEditOverlay(false)
    setConfDelete(false)
  }

  const handleSwipeProgress = (progress: number, direction: string) => {
    lastProgress.current = progress
    if (direction === 'left' || direction === 'right') {
      lastDirection.current = direction
    }
  }

  const handleSwipeEnd = () => {
    const p = lastProgress.current
    const dir = lastDirection.current
    lastProgress.current = 0
    lastDirection.current = ''

    if (dir === 'right') {
      if (p >= TRIGGER_THRESHOLD) {
        handleNavigate()
        return
      }
      if (p >= OVERLAY_THRESHOLD) {
        setConfDelete(false)
        setEditOverlay(true)
      }
      return
    }
    if (dir === 'left') {
      if (!canDelete) return
      if (p >= OVERLAY_THRESHOLD) {
        setEditOverlay(false)
        setConfDelete(true)
      }
    }
  }

  const leadingActions = (
    <LeadingActions>
      <RawSwipeAction onClick={noop} Tag='div'>
        <DualIcon>
          {canEdit ? <PencilIcon /> : <EyeIcon />}
          {canPrint && <PrintIcon />}
        </DualIcon>
      </RawSwipeAction>
    </LeadingActions>
  )

  const trailingActions = canDelete ? (
    <TrailingActions>
      <RawSwipeAction onClick={noop} Tag='div'>
        <SwipeAction
          variant='delete'
          align='end'
          icon={<TrashIcon />}
          label='מחק'
        />
      </RawSwipeAction>
    </TrailingActions>
  ) : undefined

  return (
    <Wrapper>
      <RawSwipeableListItem
        leadingActions={leadingActions}
        trailingActions={trailingActions}
        listType={Type.ANDROID}
        threshold={1.5}
        swipeStartThreshold={SWIPE_START_THRESHOLD}
        scrollStartThreshold={SCROLL_START_THRESHOLD}
        onSwipeStart={handleSwipeStart}
        onSwipeProgress={handleSwipeProgress}
        onSwipeEnd={handleSwipeEnd}
      >
        {editOverlay && (
          <EditConfirm>
            <ConfirmBtn
              type='button'
              onClick={handleNavigate}
              aria-label={modalEditText}
            >
              {canEdit ? <PencilIcon /> : <EyeIcon />}
            </ConfirmBtn>
            {canPrint && (
              <ConfirmBtn
                type='button'
                $variant='print'
                onClick={handlePrint}
                aria-label='הדפס'
              >
                <PrintIcon />
              </ConfirmBtn>
            )}
          </EditConfirm>
        )}
        <Row>
          <OrderRow
            id={OrderID}
            primary={Customer_Name?.trim() ?? ''}
            leading={<SpeedBadge active={OrderType !== 1} />}
            tag={tag}
            tagColor={tagColor}
            date={date}
            status={statusInfo}
            isLast={isLast}
          />
        </Row>
        {confDelete && <OrderDeleteConfirm onConfirm={handleConfirmDelete} />}
      </RawSwipeableListItem>
    </Wrapper>
  )
}

export default OrderRowItem

const Wrapper = styled.div`
  position: relative;
  width: 100%;

  .swipeable-list-item,
  .swipeable-list-item__content {
    width: 100%;
  }
`

const Row = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-width: 0;
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const EditConfirm = styled.div`
  flex-shrink: 0;
  align-self: stretch;
  display: inline-flex;
  animation: ${fadeIn} 0.18s ease-out;
`

const DualIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  width: 100%;
  height: 100%;
  padding: 0 1.25rem;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.ink};

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`

const ConfirmBtn = styled.button<{ $variant?: 'print' }>`
  flex-shrink: 0;
  align-self: stretch;
  width: 4.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: ${({ theme, $variant }) =>
    $variant === 'print' ? theme.colors.accentDeep : theme.colors.accent};
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  user-select: none;
  transition:
    transform ${({ theme }) => theme.motion.fast} ease,
    filter ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    filter: brightness(0.95);
  }

  &:active {
    transform: scale(0.97);
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 3.5rem;
  }
`
